import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { OptionsRender } from '50.quickConnect.Fields/FieldsTypes/Inputs/TextQcField/types';
import { useStore } from '30.quickConnect.Stores';
import { DataSourceField, SearchFamillyData } from '90.quickConnect.Models/models';
import { DataSourceType, InternalFieldState } from '90.quickConnect.Models/enums';
import { errorHandler } from '80.quickConnect.Core/helpers';
import { autoCompleteMinLength } from '50.quickConnect.Fields/const';

/**
 * Texte recherche: sans espaces de debut et de fin (le seuil de caracteres porte sur ce texte, comme sur Android)
 */
const normalizeSearchValue = (searchValue: string): string => searchValue.trim();

/**
 * Perimetre de recherche reellement envoye a l'API: forceSearchAllUO, quand il est renseigne,
 * l'emporte sur la case « Etendre la recherche » (qui n'est alors pas affichee)
 */
export const resolveSearchAllUO = (dataSource: DataSourceField | undefined, searchAllUO?: boolean): boolean =>
  dataSource?.forceSearchAllUO ?? searchAllUO ?? false;

export const useFieldAutoComplete = () => {
  // Tag
  const tag = '50.quickConnect.Fields/hooks/useFieldAutoComplete.ts';

  const [lastStringSearchWhereMoreItems, setLastStringSearchWhereMoreItems] = useState<string>('');
  const [isThereMoreItems, setIsThereMoreItems] = useState<boolean>(true);
  const [searchFieldValues, setSearchFieldValues] = useState<OptionsRender[]>([]);
  const [searchFamillyData, setSearchFamillyData] = useState<SearchFamillyData[]>([]);
  const [lastSearchAllMode, setLastSearchAllMode] = useState<boolean | undefined>(undefined);

  // Identifiant de la derniere recherche lancee par la loupe: une reponse dont l'identifiant n'est plus
  // le dernier (frappe posterieure, nouveau clic, changement de perimetre, demontage) est obsolete
  const searchRequestId = useRef<number>(0);

  // Imported Hooks
  const { t } = useTranslation('declaration');

  // Custom Hooks
  const {
    EquipmentsStore: { getSearchFieldValues, getSearchFieldValuesAzureAD },
    DeclarationStore: {
      deleteAutoCompleteForFamilly,
      deleteAutoCompleteForAAD,
      registerNewAutoCompleteForFamilly,
      registerNewAutoCompleteForAAD,
      registerNewState,
    },
  } = useStore();

  // Ce memo permet de savoir si on requete l'API ou bien on recherche dans les autoCompleteProp
  const isOkForFilterInCachedArray = useCallback(
    (searchText: string, searchAll?: boolean): boolean =>
      // If the mode 'searchAllUO' has changed since last full fetch, we cannot use cache
      (lastSearchAllMode === undefined || lastSearchAllMode === searchAll) &&
      !isThereMoreItems &&
      searchText.length >= lastStringSearchWhereMoreItems.length &&
      searchText.toLowerCase().startsWith(lastStringSearchWhereMoreItems.toLowerCase()),
    [isThereMoreItems, lastStringSearchWhereMoreItems, lastSearchAllMode],
  );

  // Filtrage des resultats deja charges, comme sur Android: la valeur, le titre, la description ou une
  // information complementaire (ex: login Azure AD) commence par le texte saisi, sans tenir compte de la casse
  const filterCachedValues = useCallback(
    (searchValue: string): OptionsRender[] => {
      const pattern = searchValue.toLowerCase();
      const startsWithPattern = (text: string | undefined): boolean => (text ?? '').toLowerCase().startsWith(pattern);

      return searchFieldValues.filter(
        ({ value, title, description, data }: OptionsRender): boolean =>
          startsWithPattern(value) ||
          startsWithPattern(title) ||
          startsWithPattern(description) ||
          Array.from(data?.values() ?? []).some(startsWithPattern),
      );
    },
    [searchFieldValues],
  );

  // Renvoie undefined si la recherche a echoue (erreur API), null si elle a ete annulee
  const getOptionsRenderForFamilyDataSource = useCallback(
    async (
      searchValue: string,
      dataSource: DataSourceField,
      searchAllUO: boolean,
    ): Promise<OptionsRender[] | null | undefined> => {
      if (isOkForFilterInCachedArray(searchValue, searchAllUO)) return filterCachedValues(searchValue);

      const { idDataSource, idsAttributesSearch, idsAttributesSelect, addSearchAttributResult } = dataSource;

      const searchResults = await getSearchFieldValues(
        idDataSource,
        idsAttributesSearch,
        idsAttributesSelect,
        searchValue,
        t,
        searchAllUO,
        addSearchAttributResult,
      );

      if (!searchResults) return searchResults;

      const newSearchFamillyData: SearchFamillyData[] = searchResults.newSearchFamillyResults.filter(
        (sr) => sr !== null,
      );

      // Enregistrer les données pour la récupération du schemaId et de l'entityInstanceId
      setSearchFamillyData(newSearchFamillyData);

      const newSearchFieldValues =
        newSearchFamillyData.map(
          ({ id, title, description, value }: SearchFamillyData) =>
            ({ id, title, description, value } as OptionsRender),
        ) ?? [];

      const newIsThereMoreItems = newSearchFieldValues.length > 0 ? searchResults.thereIsMoreItems : true;
      setIsThereMoreItems(newIsThereMoreItems);
      setSearchFieldValues(newSearchFieldValues);
      // remember the mode used for this full fetch so that subsequent starts-with filters
      // are only allowed when the mode hasn't changed
      setLastSearchAllMode(searchAllUO);

      // La liste complete correspond desormais a cette recherche, meme si une autre liste complete etait chargee
      if (newIsThereMoreItems === false) setLastStringSearchWhereMoreItems(searchValue);

      return newSearchFieldValues;
    },
    [getSearchFieldValues, isOkForFilterInCachedArray, filterCachedValues, t],
  );

  // Renvoie undefined si la recherche a echoue (erreur API), null si elle a ete annulee
  const getOptionsRenderForAAD = useCallback(
    async (searchValue: string): Promise<OptionsRender[] | null | undefined> => {
      if (isOkForFilterInCachedArray(searchValue)) return filterCachedValues(searchValue);

      // Le quatrieme argument annule la recherche precedente: une nouvelle recherche rend la
      // requete en cours obsolete.
      const searchResults = await getSearchFieldValuesAzureAD(searchValue, t, undefined, true);

      if (!searchResults) return searchResults;

      const newSearchFieldValues: OptionsRender[] = searchResults.searchUsersResults;

      const newIsThereMoreItems = newSearchFieldValues.length > 0 ? searchResults.thereIsMoreItems : true;
      setIsThereMoreItems(newIsThereMoreItems);
      setSearchFieldValues(newSearchFieldValues);

      // AAD has no 'searchAll' mode, reset/clear stored mode to avoid mismatches
      setLastSearchAllMode(undefined);

      // La liste complete correspond desormais a cette recherche, meme si une autre liste complete etait chargee
      if (newIsThereMoreItems === false) setLastStringSearchWhereMoreItems(searchValue);

      return newSearchFieldValues;
    },
    [getSearchFieldValuesAzureAD, isOkForFilterInCachedArray, filterCachedValues, t],
  );

  // Le champ est demonte: la reponse d'une recherche encore en vol ne doit plus etre affichee
  useEffect(
    () => () => {
      searchRequestId.current += 1;
    },
    [],
  );

  /**
   * Frappe dans un champ Azure AD ou famille: aucun appel API (la recherche n'est lancee que par la loupe),
   * seulement le filtrage des resultats deja charges quand la saisie precise la derniere recherche complete.
   * Une recherche lancee par la loupe et encore en vol devient obsolete.
   */
  const filterLoadedOptions = useCallback(
    (
      searchValue: string,
      dataSourceType: DataSourceType,
      dataSource: DataSourceField | undefined,
      searchAllUO?: boolean,
    ): OptionsRender[] => {
      searchRequestId.current += 1;

      const pattern = normalizeSearchValue(searchValue);
      if (pattern.length < autoCompleteMinLength) return [];

      const isOkForFilter =
        dataSourceType === DataSourceType.AzureAD
          ? isOkForFilterInCachedArray(pattern)
          : isOkForFilterInCachedArray(pattern, resolveSearchAllUO(dataSource, searchAllUO));

      return isOkForFilter ? filterCachedValues(pattern) : [];
    },
    [isOkForFilterInCachedArray, filterCachedValues],
  );

  /**
   * Invalide la recherche lancee par la loupe (ex: changement du perimetre): sa reponse ne sera pas affichee
   */
  const cancelSearchNow = useCallback(() => {
    searchRequestId.current += 1;
  }, []);

  /**
   * Clic sur la loupe: lancement de la recherche (le cache reste utilise quand la saisie precise une liste complete).
   * L'utilisateur est prevenu si la saisie est trop courte, si aucun resultat n'est trouve ou si la recherche echoue.
   *
   * @returns les propositions a afficher, ou undefined si la recherche n'est pas lancee ou est devenue obsolete
   */
  const searchOptionsNow = useCallback(
    async (
      searchValue: string,
      dataSourceType: DataSourceType,
      dataSource: DataSourceField | undefined,
      searchAllUO?: boolean,
    ): Promise<OptionsRender[] | undefined> => {
      const pattern = normalizeSearchValue(searchValue);
      if (pattern.length < autoCompleteMinLength) {
        toast.info(t('formlib_autocomplete_min_characters', { count: autoCompleteMinLength }).toString());
        return undefined;
      }

      searchRequestId.current += 1;
      const requestId = searchRequestId.current;

      // Azure AD n'a pas de perimetre de recherche
      const searchAllUOMode =
        dataSourceType === DataSourceType.AzureAD ? undefined : resolveSearchAllUO(dataSource, searchAllUO);

      // Sans reseau, pas d'appel API: l'utilisateur est prevenu (les resultats deja charges restent utilisables)
      if (!navigator.onLine && !isOkForFilterInCachedArray(pattern, searchAllUOMode)) {
        toast.warn(t('qcapp_warning_no_network', { ns: 'common' }).toString());
        return [];
      }

      let results: OptionsRender[] | null | undefined = [];
      if (dataSourceType === DataSourceType.AzureAD) results = await getOptionsRenderForAAD(pattern);
      else if (dataSource) results = await getOptionsRenderForFamilyDataSource(pattern, dataSource, !!searchAllUOMode);

      // Recherche annulee ou devenue obsolete: rien a afficher ni a signaler
      if (results === null || requestId !== searchRequestId.current) return undefined;

      if (!results) {
        toast.error(t('formlib_autocomplete_search_failed').toString());
        return [];
      }

      if (results.length === 0) toast.info(t('formlib_autocomplete_no_result').toString());

      return results;
    },
    [isOkForFilterInCachedArray, getOptionsRenderForAAD, getOptionsRenderForFamilyDataSource, t],
  );

  const deleteAutoComplete = useCallback(
    (fullPathId: string, dataSourceType: DataSourceType) => {
      if (dataSourceType === DataSourceType.AzureAD) {
        return deleteAutoCompleteForAAD(fullPathId);
      }
      return deleteAutoCompleteForFamilly(fullPathId);
    },
    [deleteAutoCompleteForFamilly, deleteAutoCompleteForAAD],
  );

  const registerNewFamillySearch = useCallback(
    (fullPathId: string, value: string) => {
      const searchFamillyElement = searchFamillyData.find(
        ({ description, title, value: searchValue }: SearchFamillyData) =>
          description === value || title === value || value === searchValue,
      );

      // Gérer l'erreur dans le cas où on ne retrouve pas l'element
      if (!searchFamillyElement) {
        const errorNotFoundElement = new Error(
          `[Client Web] - useFieldAutoComplete.ts registerNewFamillySearch method failed:  L'élément seachFamillyElement n'est pas trouvé pour la valeur ${value} et pour le fullPathId ${fullPathId}`,
        );
        errorHandler(tag, errorNotFoundElement, 'registerNewFamillySearch');

        throw errorNotFoundElement;
      }

      const { schemaId, entityInstanceId } = searchFamillyElement;

      registerNewAutoCompleteForFamilly(fullPathId, schemaId, entityInstanceId);
    },
    [searchFamillyData, tag, registerNewAutoCompleteForFamilly],
  );

  const registerValidState = useCallback(
    (fullPathId: string) => {
      registerNewState(fullPathId, InternalFieldState.Valid, true);
    },
    [registerNewState],
  );

  const unRegisterValidState = useCallback(
    (fullPathId: string) => {
      registerNewState(fullPathId, InternalFieldState.Valid, false);
    },
    [registerNewState],
  );

  const registerNewAADSearch = useCallback(
    (fullPathId: string, value: string) => {
      const elementToFind: OptionsRender | undefined = searchFieldValues.find(
        ({ value: optionValue }: OptionsRender) => optionValue === value,
      );

      if (!elementToFind) {
        const errorNotFoundElement = new Error(
          `[Client Web] - useFieldAutoComplete.ts registerNewAADSearch method failed:  L'élément n'est pas trouvé pour la valeur ${value} et pour le fullPathId ${fullPathId}`,
        );

        errorHandler(tag, errorNotFoundElement, 'registerNewAADSearch');

        throw errorNotFoundElement;
      }

      registerNewAutoCompleteForAAD(fullPathId, value);
    },
    [searchFieldValues, tag, registerNewAutoCompleteForAAD],
  );

  return {
    filterLoadedOptions,
    searchOptionsNow,
    cancelSearchNow,
    deleteAutoComplete,
    registerNewFamillySearch,
    registerNewAADSearch,
    registerValidState,
    unRegisterValidState,
  };
};
